import { createBrowserRouter, type RouteObject } from 'react-router'

import { AppShell } from '@/app/layout/AppShell'
import { PlaceholderPage } from '@/components/PlaceholderPage'
import { AcceptInvitePage } from '@/features/access/AcceptInvitePage'
import { LoginPage } from '@/features/auth/LoginPage'
import { RequireAuth } from '@/features/auth/RequireAuth'
import { Landing } from '@/features/auth/Landing'

/**
 * Route map.
 *
 * Each feature contributes its own screens from `features/<name>/routes.tsx`,
 * discovered rather than imported by name. Adding a screen is adding a file,
 * so two features being built at once never collide in here. React Router
 * ranks matches by specificity, so the order they are discovered in does not
 * matter.
 *
 * Sections that are not built yet render a PlaceholderPage naming the phase
 * that delivers them, so the shell is navigable end-to-end and nothing 404s
 * during a demo.
 */
const contributions = import.meta.glob<{ routes: RouteObject[] }>('../features/*/routes.tsx', {
  eager: true,
})

const featureRoutes: RouteObject[] = Object.values(contributions).flatMap(
  (module) => module.routes ?? [],
)

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  // Public: the recipient has no account yet, so the token is the credential.
  { path: '/invite/:token', element: <AcceptInvitePage /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Landing /> },
      ...featureRoutes,
      { path: '*', element: <PlaceholderPage title="Not found" phase={0} /> },
    ],
  },
])
